import { FC, useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap-v5'
import { ErrorMessage, Form, Formik, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { handleApiError } from '../../../../utils/handleApiError'
import { showErrorAlert } from '../../../../helpers/alert'
import { KTSVG } from '../../../../helpers/KTSVG'
import { useCurrencyField } from '../../../../hooks/components/useCurrencyField'
import { useConfirmAction } from '../../../../hooks/utils/useConfirmAction'
import { getMembershipPurchaseData, createMembership } from '../../../../services/membersAdminService'

type Props = {
    show: boolean;
    onHide: () => void;
    userId?: number;
}

export const AddMembershipModal: FC<Props> = ({ show, onHide, userId }) => {
    const confirm = useConfirmAction()
    const formikRef = useRef<any>(null)
    const [durations,setDurations] = useState<any[]>([])
    const [paymentMethods,setPaymentMethods] = useState<any[]>([])
    const [plans,setPlans] = useState<any[]>([])
    const [tiers,setTiers] = useState<any[]>([])

    useEffect(()=>{
        if (show) {
            const fetchPurchaseData = async () => {
                try {
                    const data = await getMembershipPurchaseData();
                    if (data) {
                        if (data.plans) setPlans(data.plans);
                        if (data.paymentMethods) setPaymentMethods(data.paymentMethods);
                        if (data.durations) setDurations(data.durations);
                        if (data.tiers) setTiers(data.tiers);
                    }
                } catch (error) {
                    handleApiError(error, {});
                }
            };
            fetchPurchaseData();
        }
    },[show])
    
    const initialValues = {
        user_id: userId,
        tier_id: 0,
        membership_id: 0,
        payment_method_id: 0,
        price: 0,
        price_str: '0',
        duration: 2,
        is_founding_member: true,
    };

    const validationSchema = Yup.object({
        user_id: Yup.number().moreThan(0, 'User is required').required("User is required"),
        tier_id: Yup.number().moreThan(0, 'Select member tier').required("Select member tier"),
        membership_id: Yup.number().moreThan(0, 'Select membership is required').required("Select membership is required"),
        payment_method_id: Yup.number().moreThan(0, 'Select payment type method is required').required("Select payment type method is required"),
        price: Yup.number(),
        price_str: Yup.string(),
        duration: Yup.number().moreThan(0, 'Select duration is required').required("Select duration is required"),
        is_founding_member: Yup.boolean(),
    });

    const resetForm = () => {
        if (formikRef.current) {
            formikRef.current.resetForm({
                values: { 
                    user_id: 0,
                    tier_id: 0,
                    membership_id: 0,
                    payment_method_id: 0,
                    price: 0,
                    price_str: '',
                    duration: 0,
                    is_founding_member: true,
                }
            });
        }
    }

    const onCancelHandle = () => {
        resetForm();
        onHide();
    }

    const handleSubmit = async (values) => {
        try {
            let confirmed = await confirm({
                title: `Confirm Membership Creation`,
                message: `This will add a new membership subscription to the member. Do you want to continue?`,
                btnTextAction: 'Yes, Add',
                btnTextDecline: 'Cancel',
            })
            if(confirmed){
                await createMembership(values);
                showErrorAlert({ title: 'Success', message: 'Membership created successfully.' });
                onHide();
            }
        } catch (error) {
            handleApiError(error, {});
        }
    }

    return (
        <Modal show={show} centered size="lg" backdrop="static" dialogClassName="team-admin-add-member-modal">
            <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} innerRef={formikRef}>
                {(formik) => {
                    return (<Form>
                        <div className="team-admin-add-member">
                            {/* HEADER */}
                            <div className="admin-experience-details-header">
                                <h2 className="nb-invite-modal__title">
                                    Add Membership 
                                </h2>
                                <div className="rm-style-3 cursor-pointer" onClick={onCancelHandle}>
                                    <KTSVG path="/media/svg/nobilis/closeX.svg" className="rm-style-4" />
                                </div>
                            </div>
                            {/* CONTENT */}
                            <div className="team-admin-add-member__card">
                                <div className="team-admin-add-member__field">
                                    <label>Member Tier</label>
                                    <div className='w-100 p-0 tap-add-experience-2-input-line'>
                                        <select name="tier_id" value={formik.values.tier_id} onChange={formik.handleChange} className='tap-select-add-experience w-100 p-0' placeholder='Select an option'>
                                            <option value={0} disabled hidden>Select member tier</option>
                                            {tiers.map(i=><option value={i.id}>{i.description} ({i.code})</option>)}
                                        </select>
                                    </div>
                                    <ErrorMessage name="tier_id" component="div" className="tap-add-experience-text text-danger" />
                                </div>

                                <div className="team-admin-add-member__field">
                                    <label>Membership</label>
                                    <div className='w-100 p-0 tap-add-experience-2-input-line'>
                                        <select name="membership_id" value={formik.values.membership_id} onChange={formik.handleChange} className='tap-select-add-experience w-100 p-0' placeholder='Select an option'>
                                            <option value={0} disabled hidden>Select membership</option>
                                            {plans.map(i=><option value={i.id}>{i.title}</option>)}
                                        </select>
                                    </div>
                                    <ErrorMessage name="membership_id" component="div" className="tap-add-experience-text text-danger" />
                                </div>
                                
                                <div className="team-admin-add-member__field">
                                    <label>Payment Method</label>
                                    <div className='w-100 p-0 tap-add-experience-2-input-line'>
                                        <select name="payment_method_id" value={formik.values.payment_method_id} onChange={formik.handleChange} className='tap-select-add-experience w-100 p-0' placeholder='Select an option'>
                                            <option value={0} disabled hidden>Select payment method</option>
                                            {paymentMethods.map(i=><option value={i.id}>{i.description} ({i.name})</option>)}
                                        </select>
                                    </div>
                                    <ErrorMessage name="payment_method_id" component="div" className="tap-add-experience-text text-danger" />
                                </div>

                                <div className="team-admin-add-member__field">
                                    <label>Price</label>
                                    <PriceField formik={formik} />
                                    <ErrorMessage name="price" component="div" className="tap-add-experience-text text-danger" />
                                </div>
                                
                                <div className="team-admin-add-member__field">
                                    <label>Duration</label>
                                    <div className='w-100 p-0 tap-add-experience-2-input-line'>
                                        <select name="duration" value={formik.values.duration} onChange={formik.handleChange} className='tap-select-add-experience w-100 p-0' placeholder='Select an option'>
                                            <option value={0} disabled hidden>Select duration</option>
                                            {durations.map(i=><option value={i.id}>{i.description}</option>)}
                                        </select>
                                    </div>
                                    <ErrorMessage name="duration" component="div" className="tap-add-experience-text text-danger" />
                                </div>

                                <div className='pf-checkbox mb-2'>
                                    <input type="checkbox" id={`is-member-founding`} className="pf-check" 
                                        name="is_founding_member"
                                        checked={formik.values.is_founding_member}
                                        // onChange={(e) => formik.setFieldValue('is_founding_member',e.target.checked)} />
                                        onChange={formik.handleChange} />
                                    <label htmlFor={`is-member-founding`} className="pf-checkbox-content tap-flex-center">
                                        <div className="assignment-description mt-1">Is Member Founding</div>
                                    </label>
                                    <ErrorMessage name="is_founding_member" component="div" className="tap-add-experience-text text-danger" />
                                </div>

                            </div>

                            {/* FOOTER */}
                            <div className="team-admin-add-member__footer">
                                <button type='button' className="decline-experience__btn decline-experience__btn--secondary" onClick={onCancelHandle}>
                                    Cancel
                                </button>

                                <button type='submit' className="decline-experience__btn decline-experience__btn--primary" disabled={formik.isSubmitting}>
                                    Add Membership
                                    &nbsp;<KTSVG path='/media/svg/nobilis/vector02.svg' />
                                </button>
                            </div>
                        </div>
                    </Form>);
                }}
            </Formik>
        </Modal>
    )
}

const PriceField = ({ formik }) => {
  const priceField = useCurrencyField({
    formik,
    fieldText: "price_str",
    fieldNumber: "price",
  });

  return (
    <div className="tap-add-experience-2-price-box">
      <div className="tap-add-experience-2-price-left w-100">
        <span>USD </span>
        <input
          name="price_str"
          type="text"
          value={priceField.value}
          onFocus={priceField.handleFocus}
          onChange={priceField.handleChange}
          onBlur={priceField.handleBlur}
          className="tap-add-experience-2-input"
          maxLength={10}
        />
      </div>
    </div>
  );
};